import {
  differenceInCalendarDays,
  differenceInDays,
  format,
  isBefore,
  parseISO,
  startOfDay,
  startOfWeek,
  subDays,
} from 'date-fns';

import { Class, ClassGroup } from '@/state/class-groups';
import { AvailableClass, AvailableDay } from '@/state/available-classes';
import type { AppearanceSettings } from '@/state/appearance-settings';
import type { ScheduleInstructor } from '@/helpers/instructor';
import { ONE_TIME_CLASS_CANCELED } from '@/lib/constants';
import { getErrorCode } from '@/lib/error-utils';
import {
  daysBetweenDateKeys,
  getAbbreviatedName,
  parseDateParam,
  toZonedDateKey,
} from '@/lib/utils';
import type { CalendarSlot } from '@/components/calendar';

// `CalendarSlot.id` is `slot_id ?? override_slot_id`, so an override occurrence
// carries its override id in both fields. Every request that names a class
// time (checkout, reschedule, the booking guard, the waitlist) takes its pair
// from here.
export const toOccurrenceKey = (slot: CalendarSlot) => ({
  slot_id: slot.overrideSlotId ? null : slot.id,
  override_slot_id: slot.overrideSlotId ?? null,
  start_time: slot.startTime,
});

export const linkedClassDate = (value: string | null): Date | null => {
  const date = value ? parseDateParam(value) : null;
  return date && !isBefore(date, startOfDay(new Date())) ? date : null;
};

export const getAllClasses = (data: ClassGroup[]): Class[] => {
  return data.reduce((allClasses, group) => {
    return [...allClasses, ...group.classes];
  }, [] as Class[]);
};

export const parseSelectedCsv = (value: string): string[] =>
  !value || value === 'all' ? [] : value.split(',').filter(Boolean);

// An absent kind reads as REGULAR, which keeps the month calendar: the right
// page for every class that has one.
export const isOneTimeClass = (classData: Pick<Class, 'kind'>): boolean =>
  classData.kind === 'ONE_TIME';

type BookingRule =
  | 'min_notice_hours'
  | 'max_days_in_advance'
  | 'modification_notice_hours';

type BookingRuleLevel = Partial<
  Record<BookingRule, number | null> & Record<`use_${BookingRule}`, boolean>
>;

// The backend's chain minus the schedule level, which is not public; the
// one-time form never writes it. The first level whose toggle is on wins.
export const resolveBookingRule = (
  rule: BookingRule,
  ...levels: (BookingRuleLevel | null | undefined)[]
): number | null => {
  for (const level of levels) {
    const value = level?.[rule];
    if (level?.[`use_${rule}`] && value != null) return value;
  }
  return null;
};

// The class page's 404 for a one-time class whose date was canceled, closed or
// taken by a day off. Still a 404 for every reader that only knows "gone"; the
// page reads the code to say why instead of sending the customer to the list.
export const isCanceledClassError = (error: unknown): boolean =>
  getErrorCode(error) === ONE_TIME_CLASS_CANCELED;

type OneTimeEmptyState =
  | { kind: 'past' }
  | { kind: 'started' }
  | { kind: 'closed' }
  // opensOn is the `YYYY-MM-DD` day the grid first shows the class.
  | { kind: 'not-open'; opensOn: string };

// The grid drops a class that started, sits inside min notice, or lies past max
// days ahead; only the last opens later. Days are counted in `timeZone`, the
// zone the availability request sends: the backend counts max days ahead
// there, and a studio's day can be another day for the visitor. A started
// class whose end is unknown reads as past.
export const getOneTimeEmptyState = ({
  start,
  end,
  timeZone,
  maxDaysInAdvance,
  now = new Date(),
}: {
  start: string;
  end?: string | null;
  timeZone: string;
  maxDaysInAdvance: number | null;
  now?: Date;
}): OneTimeEmptyState => {
  const startsAt = new Date(start);
  if (startsAt <= now) {
    return end && now < new Date(end) ? { kind: 'started' } : { kind: 'past' };
  }
  const startDay = toZonedDateKey(startsAt, timeZone);
  const daysAhead = daysBetweenDateKeys(
    toZonedDateKey(now, timeZone),
    startDay
  );
  return maxDaysInAdvance != null && daysAhead > maxDaysInAdvance
    ? {
        kind: 'not-open',
        opensOn: format(
          subDays(parseISO(startDay), maxDaysInAdvance),
          'yyyy-MM-dd'
        ),
      }
    : { kind: 'closed' };
};

export const doesClassMatchFilters = (
  cls: { class_id: string; instructor?: { admin_user_id: string } | null },
  classes: string[],
  instructors: string[]
): boolean =>
  (classes.length === 0 || classes.includes(cls.class_id)) &&
  (instructors.length === 0 ||
    instructors.includes(cls.instructor?.admin_user_id ?? ''));

export const getClassesByIds = (
  classGroups: ClassGroup[] | undefined,
  classIds: string[] | undefined
): Class[] => {
  if (!classIds?.length) return [];
  const ids = new Set(classIds);
  return getAllClasses(classGroups ?? []).filter((cls) => ids.has(cls.class_id));
};

export const buildDayMapByKey = (
  days: AvailableDay[]
): Map<string, AvailableDay> => {
  const map = new Map<string, AvailableDay>();
  for (const day of days) {
    map.set(format(parseISO(day.date), 'yyyy-MM-dd'), day);
  }
  return map;
};

export const getInstructorsFromAvailability = (
  availabilityData:
    | { days: AvailableDay[]; next_week_days?: AvailableDay[] }
    | undefined
) => {
  if (!availabilityData) return [];

  const instructorMap = new Map<
    string,
    {
      admin_user_id: string;
      first_name: string;
      last_name: string;
      profile_picture: string | null;
    }
  >();

  availabilityData.days.forEach((day) => {
    day.classes.forEach((cls) => {
      if (cls.instructor) {
        instructorMap.set(cls.instructor.admin_user_id, {
          admin_user_id: cls.instructor.admin_user_id,
          first_name: cls.instructor.first_name,
          last_name: cls.instructor.last_name,
          profile_picture: cls.instructor.profile_picture,
        });
      }
    });
  });

  (availabilityData.next_week_days ?? []).forEach((day) => {
    day.classes.forEach((cls) => {
      if (cls.instructor) {
        instructorMap.set(cls.instructor.admin_user_id, {
          admin_user_id: cls.instructor.admin_user_id,
          first_name: cls.instructor.first_name,
          last_name: cls.instructor.last_name,
          profile_picture: cls.instructor.profile_picture,
        });
      }
    });
  });

  return Array.from(instructorMap.values());
};

export const getInstructorListOptions = (
  allInstructors: {
    admin_user_id: string;
    first_name: string;
    last_name: string;
    profile_picture: string | null;
  }[]
) => {
  const instructorOptions = allInstructors
    .map((instructor) => ({
      label: getAbbreviatedName(instructor.first_name, instructor.last_name),
      value: instructor.admin_user_id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return instructorOptions;
};

export const getUniqueClassOptions = (allClasses: AvailableClass[]) => {
  const uniqueClasses = allClasses.reduce(
    (list, current) => {
      if (!list.some((item) => item.value === current.class_id)) {
        list.push({
          label: current.name,
          value: current.class_id,
        });
      }
      return list;
    },
    [] as { label: string; value: string }[]
  );

  return [{ label: 'All', value: 'all' }, ...uniqueClasses];
};

export const getClassListOptions = (allClasses: Class[]) => {
  const classOptions = allClasses
    .map((cls) => ({
      label: cls.name,
      value: cls.class_id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return classOptions;
};

export const getWeekStart = (date: Date): Date => {
  return startOfWeek(date, { weekStartsOn: 1 }); // Monday as first day of week
};

export const getMobileWeekStart = (date: Date): Date => {
  return subDays(date, 3);
};

export type ClosestClassMatch = {
  date: Date;
  name: string;
  instructor: { first_name: string; last_name: string } | null;
};

export const findClosestClassMatch = (
  days: AvailableDay[],
  classes: string[],
  instructors: string[],
  todayStart: Date,
  selectedDayStart: Date
): ClosestClassMatch | null => {
  let best: ClosestClassMatch | null = null;
  let bestDistance = Infinity;

  for (const day of days) {
    const dayDate = parseISO(day.date);
    if (isBefore(dayDate, todayStart)) continue;

    const cls = day.classes.find((c) =>
      doesClassMatchFilters(c, classes, instructors)
    );
    if (!cls) continue;

    const distance = Math.abs(differenceInDays(dayDate, selectedDayStart));
    if (
      distance < bestDistance ||
      (distance === bestDistance && best && isBefore(dayDate, best.date))
    ) {
      best = { date: dayDate, name: cls.name, instructor: cls.instructor };
      bestDistance = distance;
    }
  }

  return best;
};

// Whole windows from the visible one to the one holding the date, negative
// for earlier. A phone's four-day window can sit two away from a date in the
// next seven days.
export const getWindowsToDate = (
  date: Date,
  currentWeekStart: Date,
  visibleDays: number
): number =>
  Math.floor(differenceInCalendarDays(date, currentWeekStart) / visibleDays);

export const getWeekday = (
  date: Date | null
):
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY' => {
  if (!date) return 'MONDAY';
  return format(date, 'EEEE').toUpperCase() as
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';
};

export const mapSlotData = (
  slot: AvailableClass['slot'],
  instructor_schedule_id: string,
  class_id: string,
  admin_user_id: string
) => ({
  slot_id: slot.slot_id,
  override_slot_id: slot.override_slot_id,
  weekly_availability_id: '',
  instructor_schedule_id,
  class_id,
  admin_user_id,
  start_time: slot.start_time,
  end_time: slot.end_time,
  // Remaining spots, not capacity.
  participants_limit: slot.available_spots,
  total_spots: slot.total_spots,
  is_fully_booked: slot.is_fully_booked,
  waitlist_enabled: slot.waitlist_enabled,
  waitlist_count: slot.waitlist_count,
  created_at: '',
  updated_at: '',
});

export const mapWeeklyAvailability = (
  slot: AvailableClass['slot'],
  instructor_schedule_id: string,
  class_id: string,
  admin_user_id: string,
  selectedDate: Date | null
) => ({
  weekly_availability_id: '',
  instructor_schedule_id,
  class_id,
  admin_user_id,
  weekday: getWeekday(selectedDate),
  status: 'ACTIVE' as const,
  created_at: '',
  updated_at: '',
  slots: [mapSlotData(slot, instructor_schedule_id, class_id, admin_user_id)],
});

export const mapClassData = (
  item: AvailableClass,
  selectedDate: Date | null
): Class => {
  const { slot, instructor, instructor_schedule_id, ...classData } = item;

  return {
    ...classData,
    instructor_schedules: [
      {
        instructor_schedule_id,
        class_id: item.class_id,
        admin_user_id: instructor.admin_user_id,
        timezone: slot.timezone,
        use_fixed_duration_in_minutes: false,
        fixed_duration_in_minutes: 0,
        use_fixed_participants_limit: false,
        fixed_participants_limit: 0,
        created_at: '',
        updated_at: '',
        admin_user: {
          first_name: instructor.first_name,
          last_name: instructor.last_name,
          admin_user_id: instructor.admin_user_id,
          profile_picture: instructor.profile_picture,
        },
        is_substituted: item.is_substituted,
        original_instructor: item.original_instructor,
        weekly_availability: [
          mapWeeklyAvailability(
            slot,
            instructor_schedule_id,
            item.class_id,
            instructor.admin_user_id,
            selectedDate
          ),
        ],
      },
    ],
  };
};

export const cleanupOldClassSessions = (currentClassSlug: string) => {
  try {
    const allKeys = Object.keys(sessionStorage);
    const classFilterKeys = allKeys.filter((key) =>
      key.startsWith('mokoboko_class_filter_')
    );

    const otherClassKeys = classFilterKeys.filter(
      (key) => key !== `mokoboko_class_filter_${currentClassSlug}`
    );

    otherClassKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error cleaning up class sessions:', error);
  }
};

// A duration is only certain when the class has exactly one schedule and it
// pins a fixed duration; otherwise each date can differ, so show nothing.
export const getFixedDurationMinutes = (classData: Class): number | null => {
  const schedules = classData.instructor_schedules ?? [];
  return schedules.length === 1 && schedules[0].use_fixed_duration_in_minutes
    ? schedules[0].fixed_duration_in_minutes
    : null;
};

// One entry per person, since a class can hold more than one schedule of the
// same instructor. A one-time class names who teaches its one date, a cover
// before the schedule's owner.
export const getClassInstructors = (classData: Class): ScheduleInstructor[] =>
  classData.next_occurrence_instructor
    ? [classData.next_occurrence_instructor]
    : [
        ...new Map(
          (classData.instructor_schedules ?? []).flatMap((schedule) =>
            schedule.admin_user
              ? [[schedule.admin_user.admin_user_id, schedule.admin_user]]
              : []
          )
        ).values(),
      ];

const FEW_SPOTS_LEFT = 3;

// A full class says so through its badge and button, so nothing is shown at
// zero. Urgency is never claimed while nobody has booked.
export type SpotsTone = 'open' | 'few' | 'last';

export const spotsLeftOf = (
  left: number | null | undefined,
  total: number
): { label: string; tone: SpotsTone } | null => {
  const spots = left ?? 0;
  if (spots <= 0) return null;
  const booked = spots < total;
  if (booked && spots === 1) return { label: 'Last spot', tone: 'last' };
  if (booked && spots <= FEW_SPOTS_LEFT)
    return { label: `${spots} spots left`, tone: 'few' };
  return { label: `${spots}/${total} spots`, tone: 'open' };
};

// The class cards and their skeletons read the switches here, so a default
// (the list description shows unless turned off) cannot differ between them.
export const classCardFlagsOf = (
  settings: AppearanceSettings['class'] | undefined
) => ({
  showImage: !!(settings?.show_image_in_list ?? settings?.show_image),
  showPrice: !!(settings?.show_price_in_list ?? settings?.show_price),
  showDuration: !!(settings?.show_duration_in_list ?? settings?.show_duration),
  showDescription: settings?.show_description_in_list ?? true,
  showSpots: !!(
    settings?.show_available_slots_in_list ?? settings?.show_available_slots
  ),
  showInstructor: !!(
    settings?.show_instructor_name_in_list ?? settings?.show_instructor_name
  ),
});
